import { FC, memo, useCallback, useMemo, useState } from "react";
import { Button, Collapse, Divider, Stack, Typography, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IconCheckCircleFill, IconChevronDown } from "@/assets/icons";
import { background, divider, orange, primary, text, yellow } from "@/theme/colors";
import { rounded, spacing } from "@/theme/variables";
import { typography } from "@/theme/typography";
import { AppRoutes } from "@/routes/config";
import { TSubjectCurriculumDto, TSubjectStatusDto } from "@/api/services/main/curriculum-progress/dtos/get-curriculum-progress.dto";
import { LevelProgressBar } from "@/features/dashboard/components/learning-progress/LevelProgressBar";
import { isSkipStatus } from "@/features/dashboard/utils/curriculumLevel";
import { navigateToRecommendation } from "@/features/study/utils/navigateToRecommendation";

type TSubjectCurriculumAccordionProps = {
  subject: TSubjectCurriculumDto;
  isRecommended?: boolean;
  isDisabled?: boolean;
};

const getRedirectLevel = (levels: TSubjectCurriculumDto["levels"]): string => {
  const consideredLevels = levels.filter((l) => !isSkipStatus(l.status));
  const inProgressLevel = consideredLevels.find((l) => l.status === "in_progress");
  if (inProgressLevel) return inProgressLevel.level;
  const notStartedLevel = consideredLevels.find((l) => l.status === "not_started");
  if (notStartedLevel) return notStartedLevel.level;
  return consideredLevels[consideredLevels.length - 1].level;
};

/** WEEK card fill + outline per status — 完了 teal, 着手中 orange, 未着手 grey */
const STATUS_STYLES: Record<TSubjectStatusDto, { bgcolor: string; borderColor: string }> = {
  completed: { bgcolor: background.secondary, borderColor: primary[500] },
  in_progress: { bgcolor: yellow[50], borderColor: orange[80] },
  not_started: { bgcolor: background.gray, borderColor: divider.high },
};

/** Get accordion container styles based on subject status */
const getContainerStyles = (subject: TSubjectCurriculumDto, isRecommended: boolean, isDisabled: boolean) => {
  if (isDisabled) {
    return {
      bgcolor: background.disable,
      border: `1px solid ${divider.high}`,
      pointerEvents: "none" as const,
    };
  }

  const { bgcolor, borderColor } = STATUS_STYLES[subject.status];
  // The recommended WEEK keeps the orange outline over its status fill
  return { bgcolor, border: `1px solid ${isRecommended ? orange[80] : borderColor}` };
};

const SubjectCurriculumAccordionComponent: FC<TSubjectCurriculumAccordionProps> = ({ subject, isRecommended = false, isDisabled = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAssessed = subject.assessedLevel !== null;
  /* Always starts collapsed, including the recommended WEEK */
  const [expanded, setExpanded] = useState(false);
  const hasContent = subject.levels.length > 0;
  const canExpand = hasContent && !isDisabled;

  const handleNavigateToSubject = useCallback(() => {
    const subjectId = String(subject.id);
    if (isAssessed) {
      navigateToRecommendation(navigate, {
        publishedSubjectId: subject.id,
        level: getRedirectLevel(subject.levels),
        type: "LEVEL_QUIZ",
      });
    } else {
      navigate(AppRoutes.studySubject.replace(":subjectId", subjectId));
    }
  }, [navigate, subject.id, subject.levels, isAssessed]);

  const handleToggleExpand = useCallback(() => {
    if (!canExpand) return;
    setExpanded((expanded) => !expanded);
  }, [canExpand]);

  const containerStyles = useMemo(() => getContainerStyles(subject, isRecommended, isDisabled), [subject, isRecommended, isDisabled]);

  return (
    <Stack
      aria-disabled={isDisabled || undefined}
      sx={{
        borderRadius: rounded.xs,
        px: spacing.sm,
        py: spacing.xs,
        minHeight: 48,
        justifyContent: "center",
        ...containerStyles,
      }}
    >
      {/* Weekly subject label */}
      {isRecommended && (
        <Stack sx={{ mb: spacing.xs }}>
          <Typography sx={{ ...typography.sub16Bold, color: orange[80], mb: spacing.xs }}>{t("dashboard.learningProgress.weeklySubject")}</Typography>
          <Divider sx={{ borderBottom: `1px solid ${divider.high}` }} />
        </Stack>
      )}

      {/* Header row */}
      <Stack display="flex" flexDirection="row" sx={{ cursor: canExpand ? "pointer" : "default" }} onClick={handleToggleExpand}>
        {/* Check icon for completed subjects */}
        {subject.status === "completed" && <IconCheckCircleFill color={primary[500]} width={20} height={20} />}

        <Typography variant="body14Semi" marginLeft={spacing.xxs} color={text.primary} flex={1}>
          {subject.name}
        </Typography>

        {canExpand && (
          <Stack
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
              flexShrink: 0,
            }}
          >
            <IconChevronDown width={24} height={24} />
          </Stack>
        )}
      </Stack>

      {/* Collapsible content — no gap when closed */}
      {canExpand && (
        <Collapse in={expanded} timeout={200} unmountOnExit>
          <Stack gap={2} pt={spacing.sm}>
            <LevelProgressBar levels={subject.levels} disabled={!isAssessed} />

            <Stack alignItems="center">
              <StyledButton variant="outlined" onClick={handleNavigateToSubject}>
                {isAssessed ? t("dashboard.learningProgress.goToSubject") : t("dashboard.learningProgress.takeDiagnosis")}
              </StyledButton>
            </Stack>
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
};

export const SubjectCurriculumAccordion = memo(SubjectCurriculumAccordionComponent);

// Styled Button for SubjectCurriculumAccordion
const StyledButton = styled(Button)(() => ({
  borderRadius: rounded.sm,
  border: `2px solid ${primary[500]}`,
  color: primary[500],
  backgroundColor: background.white,
  ...typography.body14Semi,
  paddingLeft: spacing.sm,
  paddingRight: spacing.sm,
  paddingTop: "10px",
  paddingBottom: "10px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: primary[900],
    border: `2px solid ${primary[500]}`,
  },
}));
